// ===============================================================
// ProBank — Card Application Page
//
// Real bank flow:
//   1. Pick a card tier (Classic $43 / Gold $55 / Platinum $75)
//      — each card can be tapped to flip and preview its back
//   2. Review applicant details (cardholder name editable)
//   3. See the fee converted live to local currency
//   4. Confirm → instructions modal (copy amount, 5s countdown)
//   5. Payment link opens (same as subscriptions)
//   6. Application saved as PENDING — admin approves after the
//      manual payment arrives.
// ===============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCountry } from "../context/CountryContext";
import API from "../utils/api";
import VirtualCardDisplay from "../components/VirtualCardDisplay";

// Same payment link as subscriptions
const PAYMENT_LINK = "https://flutterwave.com/pay/prorealalertsubscription";

const TIERS = [
  {
    key: "classic",
    label: "Classic",
    feeUSD: 43,
    perks: ["Shop online worldwide", "Freeze anytime", "Instant issuance after approval"],
  },
  {
    key: "gold",
    label: "Gold",
    feeUSD: 55,
    perks: ["Everything in Classic", "Priority approval", "Premium gold design"],
  },
  {
    key: "platinum",
    label: "Platinum",
    feeUSD: 75,
    perks: ["Everything in Gold", "Top-priority support", "Exclusive platinum design"],
  },
];

const FloatingNotice = ({ message, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-600/90 text-white px-6 py-3 rounded-xl shadow-xl z-50"
  >
    <div className="flex items-center justify-between gap-4">
      <span>{message}</span>
      <button onClick={onClose} className="font-bold text-white">×</button>
    </div>
  </motion.div>
);

export default function CardApplyPage() {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [user, setUser] = useState(null);
  const [cardholderName, setCardholderName] = useState("");
  const [selectedTier, setSelectedTier] = useState("classic");
  const [flippedTier, setFlippedTier] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(isGhana ? 14 : 1600);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsCountdown, setInstructionsCountdown] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");

  const currencySymbol = isGhana ? "₵" : "₦";
  const currencyCode = isGhana ? "GHS" : "NGN";
  const flag = isGhana ? "🇬🇭" : "🇳🇬";

  const tier = TIERS.find((t) => t.key === selectedTier);

  // Load user (cached first, then fresh)
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("user") || "{}");
      if (cached && cached.name) {
        setUser(cached);
        setCardholderName((cached.name || "").toUpperCase());
      }
    } catch (err) {}

    const refresh = async () => {
      try {
        const res = await API.get("/dashboard");
        const fresh = res.data?.user;
        if (fresh) {
          setUser(fresh);
          setCardholderName((fresh.name || "").toUpperCase());
          localStorage.setItem("user", JSON.stringify(fresh));
        }
      } catch (err) {
        console.warn("User refresh failed:", err);
      }
    };
    refresh();
  }, []);

  // Live exchange rate — same source as SubscriptionPage
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
        const data = await res.json();
        const code = isGhana ? "GHS" : "NGN";
        if (data?.rates?.[code]) {
          setExchangeRate(data.rates[code]);
        }
      } catch (err) {
        console.error("Rate fetch failed", err);
      }
    };
    fetchRate();
  }, [isGhana]);

  // Instructions countdown
  useEffect(() => {
    if (!showInstructions) return;
    setInstructionsCountdown(5);
    const timer = setInterval(() => {
      setInstructionsCountdown((p) => {
        if (p <= 1) {
          clearInterval(timer);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showInstructions]);

  const localAmount = Math.round(tier.feeUSD * exchangeRate);
  const localAmountDisplay = localAmount.toLocaleString();

  const localFor = (usd) => Math.round(usd * exchangeRate).toLocaleString();

  const copyAmount = () => {
    navigator.clipboard.writeText(String(localAmount));
    setNotice(`✅ ${currencySymbol}${localAmountDisplay} copied!`);
    setTimeout(() => setNotice(""), 2500);
  };

  const handleSelectTier = (key) => {
    if (selectedTier === key) {
      // second tap on the selected card → flip preview
      setFlippedTier((f) => (f === key ? null : key));
    } else {
      setSelectedTier(key);
      setFlippedTier(null);
    }
  };

  // Confirm → save application as pending → show instructions
  const confirmAndProceed = async () => {
    if (!cardholderName.trim()) {
      setNotice("Please enter the name for your card");
      setTimeout(() => setNotice(""), 3000);
      return;
    }

    setSubmitting(true);
    try {
      await API.post("/cards/apply", {
        cardholderName: cardholderName.trim(),
        feeAmount: localAmount,
        feeCurrency: currencyCode,
        tier: selectedTier,
      });

      setIsConfirmOpen(false);
      setShowInstructions(true);
    } catch (err) {
      const msg = err.response?.data?.message || "Could not submit application";
      if (msg.includes("already have")) {
        navigate("/cards");
        return;
      }
      setNotice(msg);
      setTimeout(() => setNotice(""), 3500);
    } finally {
      setSubmitting(false);
    }
  };

  const goToPayment = () => {
    window.location.href = PAYMENT_LINK;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#071032] to-black text-white pb-16">
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-8">
        {/* header */}
        <button
          onClick={() => navigate("/cards")}
          className="text-slate-400 text-sm mb-4 hover:text-white transition"
        >
          ← Back to Cards
        </button>
        <h1 className="text-2xl font-bold">Choose Your Card</h1>
        <p className="text-slate-400 text-sm mt-1">
          Select a card — tap the selected card again to flip it.
        </p>

        {/* ============ TIER PICKER ============ */}
        <div className="mt-6 space-y-6">
          {TIERS.map((t) => {
            const isSelected = selectedTier === t.key;
            return (
              <div key={t.key} className="relative">
                <VirtualCardDisplay
                  tier={t.key}
                  holderName={cardholderName || "YOUR NAME"}
                  status="active"
                  flipped={flippedTier === t.key}
                  selected={isSelected}
                  onFlip={() => handleSelectTier(t.key)}
                />

                {/* tier info row */}
                <div
                  className={`mt-3 rounded-2xl border p-4 transition ${
                    isSelected
                      ? "border-amber-400/50 bg-amber-500/10"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? "border-amber-400" : "border-slate-500"
                        }`}
                      >
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />}
                      </div>
                      <span className="font-bold">{t.label}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-extrabold text-amber-300">${t.feeUSD}</span>
                      <span className="text-slate-400 text-xs ml-1.5">
                        ≈ {currencySymbol}{localFor(t.feeUSD)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
                    {t.perks.map((perk, i) => (
                      <span key={i} className="text-[11px] text-slate-400 flex items-center gap-1">
                        <span className="text-emerald-400">✓</span> {perk}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ============ APPLICANT DETAILS ============ */}
        <h2 className="text-lg font-bold mt-9">Your Details</h2>
        <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/10">
          <div className="px-4 py-3.5">
            <label className="text-slate-400 text-xs block mb-1.5">
              Name on card (you can edit)
            </label>
            <input
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
              maxLength={26}
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm font-semibold tracking-wide outline-none focus:border-amber-400/60 transition uppercase"
              placeholder="NAME ON CARD"
            />
          </div>
          <div className="flex justify-between px-4 py-3.5">
            <span className="text-slate-400 text-sm">Email</span>
            <span className="text-sm font-medium break-all text-right ml-4">
              {user?.email || "—"}
            </span>
          </div>
          <div className="flex justify-between px-4 py-3.5">
            <span className="text-slate-400 text-sm">Country</span>
            <span className="text-sm font-medium">{isGhana ? "Ghana" : "Nigeria"}</span>
          </div>
        </div>

        {/* ============ FEE ============ */}
        <div className="mt-5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-amber-200 font-semibold">
                {tier.label} card fee
              </span>
              <div className="text-[11px] text-slate-400 mt-0.5">One-time payment</div>
            </div>
            <div className="text-right">
              <div className="text-amber-300 font-extrabold text-lg">${tier.feeUSD}</div>
              <div className="text-white font-bold text-sm">
                ≈ {currencySymbol}{localAmountDisplay}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
            Paid manually via our payment page • Your card is activated by
            support once payment is confirmed.
          </p>
        </div>

        {/* ============ APPLY BUTTON ============ */}
        <button
          onClick={() => {
            if (!cardholderName.trim()) {
              setNotice("Please enter the name for your card");
              setTimeout(() => setNotice(""), 3000);
              return;
            }
            setIsConfirmOpen(true);
          }}
          className="mt-6 w-full py-4 rounded-2xl bg-gradient-to-r from-amber-400 to-amber-600 text-black font-bold text-base shadow-lg shadow-amber-500/20 active:scale-[0.98] transition"
        >
          Apply — Pay {currencySymbol}{localAmountDisplay}
        </button>

        <p className="text-center text-[11px] text-slate-500 mt-3 mb-4">
          Manual payment • Activated by support after confirmation
        </p>
      </div>

      {/* ============ CONFIRM MODAL ============ */}
      <AnimatePresence>
        {isConfirmOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-sm w-full text-center"
            >
              <h2 className="text-2xl font-bold mb-4">Confirm Application</h2>

              <p className="text-gray-400 mb-1 text-sm">
                {tier.label} card for
              </p>
              <p className="text-white font-bold mb-4 tracking-wide">{cardholderName}</p>

              <p className="text-amber-300 font-bold text-lg mb-1">
                ${tier.feeUSD} ≈ {currencySymbol}{localAmountDisplay}
              </p>
              <p className="text-xs text-gray-500 mb-6">
                Pricing for {flag} {config.name}
              </p>

              <div className="flex gap-4 justify-center flex-wrap">
                <button
                  onClick={confirmAndProceed}
                  disabled={submitting}
                  className="bg-amber-400 text-black px-6 py-3 rounded-xl font-semibold disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Proceed to Pay"}
                </button>
                <button
                  onClick={() => setIsConfirmOpen(false)}
                  className="bg-zinc-700 px-6 py-3 rounded-xl text-white"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ INSTRUCTIONS MODAL ============ */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 max-w-md w-full space-y-5 my-4"
            >
              <div className="text-center">
                <div className="text-4xl mb-1">📋</div>
                <h2 className="text-xl font-bold text-white">Follow These Steps</h2>
                <p className="text-gray-400 text-xs mt-1">Takes 30 seconds</p>
              </div>

              {/* COPY AMOUNT */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 border-green-400/40 space-y-3">
                <p className="text-center text-green-300 font-semibold text-sm">
                  Step 1: Copy the amount
                </p>
                <button
                  onClick={copyAmount}
                  className="w-full p-3 rounded-xl bg-black/40 border border-green-400/30 hover:bg-black/60 transition flex items-center justify-between"
                >
                  <span className="text-2xl font-bold text-green-400">
                    {currencySymbol}{localAmountDisplay}
                  </span>
                  <span className="text-xs bg-green-500 text-black font-bold px-3 py-2 rounded-lg">
                    📋 TAP TO COPY
                  </span>
                </button>
                <p className="text-center text-xs text-green-200/80">
                  {tier.label} card fee — ${tier.feeUSD}
                </p>
              </div>

              {/* STEPS */}
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-400/30 space-y-2">
                <p className="text-blue-300 font-semibold text-sm">📲 On the payment page:</p>
                <ol className="text-blue-100 text-sm space-y-2 pl-1">
                  <li>
                    <b className="text-blue-400">2.</b> Tap the{" "}
                    <span className="font-bold text-white bg-white/10 px-2 py-0.5 rounded">Amount</span>{" "}
                    field and <b className="text-white">paste</b>{" "}
                    <b className="text-green-300">{localAmountDisplay}</b>
                  </li>
                  <li>
                    <b className="text-blue-400">3.</b> Choose your payment option (Card, Transfer, USSD)
                  </li>
                  <li>
                    <b className="text-blue-400">4.</b> Complete the payment
                  </li>
                  <li>
                    <b className="text-blue-400">5.</b> Send your payment proof to support
                  </li>
                </ol>
              </div>

              <div className="p-3 rounded-xl bg-yellow-500/15 border border-yellow-400/40 text-yellow-200 text-xs text-center">
                💡 Your {tier.label} card will be activated by support after payment is confirmed.
              </div>

              <button
                onClick={goToPayment}
                disabled={instructionsCountdown > 0}
                className={`w-full py-4 rounded-xl font-bold text-base shadow-lg ${
                  instructionsCountdown > 0
                    ? "bg-gray-600 cursor-not-allowed text-gray-300"
                    : "bg-gradient-to-r from-amber-400 to-amber-600 hover:brightness-110 text-black active:scale-[0.98]"
                }`}
              >
                {instructionsCountdown > 0
                  ? `Please read first... (${instructionsCountdown}s)`
                  : "✅ Got It — Take Me to Payment"}
              </button>

              <button
                onClick={() => {
                  setShowInstructions(false);
                  navigate("/cards");
                }}
                className="w-full py-1 text-xs text-gray-400 hover:text-white"
              >
                ← I'll pay later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {notice && <FloatingNotice message={notice} onClose={() => setNotice("")} />}
      </AnimatePresence>
    </div>
  );
}
